import { ROUTE } from '../../../../routes';
import type { SiteSeoSummary } from '../../../seo/seo-types';
import type { WebsiteCardIntent, WebsiteCardSummary } from '../../card-summary';

/**
 * What the SEO card says, from the light `site-seo/summary` read (never the full state). Pure: copy is an id, mapped
 * to its key in `seo-copy.ts`.
 */

export type SeoCardInput = {
  /** `unavailable`: the summary route answered 404 (not deployed, or the service is not configured here). */
  status: 'loading' | 'failed' | 'unavailable' | 'ready';
  summary: SiteSeoSummary | null;
};

export type SeoCopy =
  | { id: 'unpublished-changes' }
  | { id: 'publishing' }
  | { id: 'not-on-site' }
  | { id: 'pages'; n: number; m: number }
  | { id: 'seo-unavailable' }
  | { id: 'not-set-up' }
  | { id: 'open-seo' }
  | { id: 'review-and-publish' };

const OPEN_SEO: WebsiteCardIntent<SeoCopy> = { copy: { id: 'open-seo' }, to: ROUTE.seo() };
const REVIEW_AND_PUBLISH: WebsiteCardIntent<SeoCopy> = { copy: { id: 'review-and-publish' }, to: ROUTE.seo() };

const contextLine = (line: SeoCopy): WebsiteCardSummary<SeoCopy> => ({
  chip: null,
  body: { kind: 'context', line },
  primary: OPEN_SEO,
  secondary: null,
});

const waiting = (id: 'not-on-site' | 'unpublished-changes'): WebsiteCardSummary<SeoCopy> => ({
  chip: { tone: 'waiting', copy: { id } },
  body: { kind: 'waiting', lines: [{ id }] },
  primary: REVIEW_AND_PUBLISH,
  secondary: null,
});

/**
 * In order: no site (404, or `available: false`), no document yet, a publish under way, a publish the site may not
 * carry (Decision 18, ahead of unpublished changes and even when clean), unpublished changes, then the counts.
 */
export const deriveSeoSummary = (input: SeoCardInput): WebsiteCardSummary<SeoCopy> => {
  const { status, summary } = input;
  if (status === 'unavailable') return contextLine({ id: 'seo-unavailable' });
  if (status !== 'ready' || summary === null) {
    return {
      chip: null,
      body: { kind: status === 'loading' ? 'loading' : 'failed' },
      primary: OPEN_SEO,
      secondary: null,
    };
  }
  if (!summary.available) return contextLine({ id: 'seo-unavailable' });
  if (!summary.has_document) return contextLine({ id: 'not-set-up' });
  if (summary.publishing !== null) return contextLine({ id: 'publishing' });
  if (!summary.confirmed) return waiting('not-on-site');
  if (summary.dirty) return waiting('unpublished-changes');
  return contextLine({ id: 'pages', n: summary.pages, m: summary.without_description });
};
